import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import '../../GraphRead/graph.css';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import { Alert, MenuItem, SwipeableDrawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { EditInterview } from './EditInterview.jsx';
import { getEdgeCategoryColor, getEdgeCategoryLabel, getEdgeCategoryOptions, normalizeEdgeCategory } from '../../edgeCategories';
import { normalizeGraphType } from '../../graphTypes';
import { getColor } from '../utils';

const EDIT_EDGE_MUTATION = gql`
mutation EditEdge($edgeId: ID!, $notes: String!, $dateMet: Date!, $category: String, $images: [ImageInput], $interviewQuestions: [InterviewQuestionInput]) {
  editEdge(edgeId: $edgeId, notes: $notes, dateMet: $dateMet, category: $category, images: $images, interviewQuestions: $interviewQuestions) {
    _id
  }
}
`;

const DELETE_EDGE_MUTATION = gql`
mutation DeleteEdge($edgeId: ID!) {
  deleteEdge(edgeId: $edgeId)
}
`;

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';
}

function PersonPhoto({ person, className = '' }) {
  const [didImageFail, setDidImageFail] = useState(false);
  const imageURL = person?.imageURL;

  useEffect(() => {
    setDidImageFail(false);
  }, [imageURL]);

  return (
    <div
      className={`graph-detail-avatar ${className}`}
      style={{ '--detail-accent': getColor(person?._id) }}
      aria-hidden={!person?.name}
    >
      {imageURL && !didImageFail ? (
        <img src={imageURL} alt={person?.name || ''} onError={() => setDidImageFail(true)} />
      ) : (
        <span>{getInitials(person?.name)}</span>
      )}
    </div>
  );
}

export const EditEdgeModal = ({
  selectedEdge,
  isModalOpen,
  setIsModalOpen,
  refetchGraph,
  person1,
  person2,
  graphType = 'social',
}) => {
  const [editEdge, { loading: isSaving, error: saveError }] = useMutation(EDIT_EDGE_MUTATION);
  const [deleteEdge, { loading: isDeleting, error: deleteError }] = useMutation(DELETE_EDGE_MUTATION);
  const [editedEdgeNote, setEditedEdgeNote] = useState('');
  const [editedEdgeDateMet, setEditedEdgeDateMet] = useState();
  const [editedEdgeCategory, setEditedEdgeCategory] = useState('friendship');
  const categoryOptions = useMemo(() => getEdgeCategoryOptions(normalizeGraphType(graphType)), [graphType]);
  const defaultCategory = categoryOptions[0]?.value || 'friendship';

  const [interviewQuestions, setInterviewQuestions] = useState([]);


  const saveChanges = async () => {
    const dateMet = dayjs.isDayjs(editedEdgeDateMet) ? editedEdgeDateMet.toDate() : editedEdgeDateMet;
    await editEdge({ variables: { edgeId: selectedEdge._id, notes: editedEdgeNote, dateMet, category: editedEdgeCategory,
      interviewQuestions: interviewQuestions.map(({question, answer, personId}) => ({question, answer, personId})) } });
    await refetchGraph();
    setIsModalOpen(false);
  };

  const deleteRelationship = async () => {
    const confirmed = window.confirm(`Delete the story between ${person1?.name || 'these people'} and ${person2?.name || 'these people'}?`);
    if (!confirmed) return;

    await deleteEdge({ variables: { edgeId: selectedEdge._id } });
    await refetchGraph();
    setIsModalOpen(false);
  };

  useEffect(() => {
    setEditedEdgeNote(selectedEdge?.note);
    setEditedEdgeDateMet(selectedEdge?.dateMet ? dayjs(selectedEdge.dateMet) : null);
    const nextCategory = normalizeEdgeCategory(selectedEdge?.category) || defaultCategory;
    setEditedEdgeCategory(categoryOptions.some((option) => option.value === nextCategory) ? nextCategory : defaultCategory);
    setInterviewQuestions(selectedEdge?.interviewQuestions || []);
  }, [selectedEdge, defaultCategory, categoryOptions]);


  if (!selectedEdge || !isModalOpen) return null;
  const categoryColor = getEdgeCategoryColor(editedEdgeCategory);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onOpen={() => setIsModalOpen(true)}
      swipeAreaWidth={selectedEdge ? 48 : 0}
      disableSwipeToOpen={false}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          height: 'min(86dvh, calc(100dvh - env(safe-area-inset-top, 0px)))',
          overflow: 'visible',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <section
        className="graph-detail-sheet graph-edge-sheet graph-edit-detail-sheet"
        style={{
          '--detail-accent': categoryColor,
          '--detail-left': getColor(person1?._id),
          '--detail-right': getColor(person2?._id),
          '--detail-accent-soft': `color-mix(in srgb, ${categoryColor} 24%, transparent)`,
        }}
        aria-label={person1 && person2 ? `Edit connection between ${person1.name} and ${person2.name}` : 'Edit connection'}
      >
        <div className="graph-detail-puller" />
        <button type="button" className="graph-detail-close" onClick={() => setIsModalOpen(false)} aria-label="Close connection editor">
          <CloseIcon fontSize="small" />
        </button>

        <header className="graph-edge-hero">
          <div className="graph-edge-person graph-edit-edge-person">
            <PersonPhoto person={person1} className="graph-detail-avatar--edge" />
            <strong>{person1?.name || 'Unknown'}</strong>
          </div>
          <div className="graph-edge-connector" aria-hidden="true">
            <span />
            <LinkIcon fontSize="small" />
            <span />
          </div>
          <div className="graph-edge-person graph-edit-edge-person">
            <PersonPhoto person={person2} className="graph-detail-avatar--edge" />
            <strong>{person2?.name || 'Unknown'}</strong>
          </div>
        </header>

        <div className="graph-edge-title-row">
          <div>
            <span>Edit connection</span>
            <h2>{person1?.name || 'Someone'} met {person2?.name || 'someone'}</h2>
          </div>
          <strong className="graph-detail-category" style={{ '--edge-category-color': categoryColor }}>
            {getEdgeCategoryLabel(editedEdgeCategory)}
          </strong>
        </div>

        <form className="graph-edit-drawer-form" onSubmit={(event) => {
          event.preventDefault();
          saveChanges();
        }}>
            {(saveError || deleteError) && (
              <Alert severity="error">
                {saveError?.message || deleteError?.message}
              </Alert>
            )}
            <div className="graph-detail-section-header">
              <span>Story</span>
              <strong>Edit how they met</strong>
            </div>
            <Box className="graph-edit-field-grid">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="When We Met"
                  value={editedEdgeDateMet}
                  onChange={(e) => setEditedEdgeDateMet(e)}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
              <TextField
                select
                label="Meeting category"
                value={editedEdgeCategory}
                onChange={(e) => setEditedEdgeCategory(e.target.value)}
                fullWidth
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              type="text"
              multiline
              value={editedEdgeNote}
              onChange={(e) => setEditedEdgeNote(e.target.value)}
              label="How We Met"
              fullWidth
              minRows={3}
            />
            {interviewQuestions && <EditInterview
              questions={interviewQuestions}
              setQuestions={setInterviewQuestions}
              person1={person1}
              person2={person2}

            />}

          <div className="graph-edit-drawer-actions">
            <Button type="submit" variant="contained" disabled={isSaving || isDeleting || !editedEdgeNote || !editedEdgeDateMet}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={() => setIsModalOpen(false)} disabled={isSaving || isDeleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deleteRelationship} disabled={isSaving || isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </form>
      </section>
    </SwipeableDrawer>
  );
};
